// src/pages/Entreprise.js
import React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, slideInLeft, slideInRight, scaleIn } from '../styles/animations';
import SectionTitle from '../components/ui/SectionTitle';

const EntreprisePageWrapper = styled.div`
  padding-top: 80px; // Pour compenser la hauteur du header fixe
`;

const HeroSection = styled.section`
  position: relative;
  height: 50vh;
  min-height: 400px;
  max-height: 600px;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7));
    z-index: 1;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  transform: translateY(-50%);
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.light};
  z-index: 2;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const HeroTitle = styled.h1`
  color: ${props => props.theme.colors.light};
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.xxxl};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    font-size: ${props => props.theme.fontSizes.xxl};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: 0;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto;
  color: ${props => props.theme.colors.light};
`;

const Section = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.bgColor || props.theme.colors.light};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const AboutContent = styled.div`
  animation: ${slideInLeft} 0.8s ease-out forwards;
  opacity: 0;
  
  p {
    margin-bottom: ${props => props.theme.spacing.md};
    line-height: 1.7;
  }
`;

const AboutImageContainer = styled.div`
  position: relative;
  animation: ${slideInRight} 0.8s ease-out forwards;
  opacity: 0;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    order: -1;
    margin-bottom: ${props => props.theme.spacing.md};
  }
`;

const AboutImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
`;

const ExpertiseSection = styled.section`
  padding: ${props => props.theme.spacing.xl} 0;
  background-color: ${props => props.theme.colors.gray};
`;

const ExpertiseList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: ${props => props.theme.spacing.lg};
`;

const ExpertiseItem = styled.li`
  margin-bottom: ${props => props.theme.spacing.md};
  padding-left: 2rem;
  position: relative;
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.5rem;
    width: 6px;
    height: 6px;
    background-color: ${props => props.theme.colors.primary};
    border-radius: 50%;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.lg} 0;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ImageItem = styled.div`
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const Entreprise = () => {
    return (
        <EntreprisePageWrapper>
            <Helmet>
                <title>Notre Entreprise - Decko | Créateur de vos parquets de jardin</title>
                <meta name="description" content="DeckO crée, rénove et agrandit des terrasses depuis 2004. Spécialistes bois, composite et céramique sur la région nantaise." />
            </Helmet>

            <HeroSection>
                <HeroImage src="https://images.unsplash.com/q=80&w=2000" alt="L'équipe Decko" />
                <HeroContent>
                    <HeroTitle>Qui sommes nous ?</HeroTitle>
                    <HeroSubtitle>DeckO crée, rénove ou agrandit des terrasses depuis 2004</HeroSubtitle>
                </HeroContent>
            </HeroSection>

            <Section>
                <SectionContainer>
                    <AboutGrid>
                        <AboutContent>
                            <SectionTitle text="Notre Histoire" />
                            <p>Professionnel du bois depuis 1985, j'ai fondé DeckO en 2004 parce que créer et construire des « parquets » de jardin de qualité est un vrai métier.</p>
                            <p>Aux États-Unis, « the deck builders » rencontrés en 2003 sur leur salon annuel (traduisez « les constructeurs de terrasses ») exploitent à grande échelle une caractéristique de l'habitat traditionnel Nord Américain. Outre-Atlantique, cette profession est donc l'affaire de vrais spécialistes. Pourquoi pas en France ?</p>
                            <SectionTitle text="Rester Curieux" />
                            <p>DeckO est spécialiste ce qui implique un savoir-faire particulier alliant une connaissance de la matière, une exigence de qualité et une vision créative pour vos espaces de convivialité. C'est aussi savoir se documenter, arpenter des salons professionnels et écouter des fournisseurs internationaux.</p>
                            <p>Des alternatives de matériaux avec le bois-composite employé dès 2008 et la céramique sur plots en 2013 élargissent les solutions décoratives et réponses techniques pour toutes les exigences.</p>
                            <p>Depuis sa création DeckO complète ses compétences sur le travail de l'inox, de l'aluminium et du verre pour les garde-corps avec des gammes élégantes et novatrices.</p>
                            <SectionTitle text="Venez nous voir" />
                            <p>Artisans passionnés, nous allions savoir-faire traditionnel et matériaux modernes pour créer des espaces extérieurs confortables et durables. Chantier propre, délais respectés.</p>
                            <p>Nous vous accueillerons sur RDV à l'atelier à deux pas de l'usine LU et RN249 Direction Cholet ou TER Tram-Train Arrêt La Haie-Fouassière (16mn Nantes Centre puis 6mn à pied).</p>
                            <p><strong>DeckO Terrasses</strong> — ZA du Patis, 20, rue du Patis 44690 La Haie-Fouassière<br />Tél. 06 83 58 99 05</p>
                        </AboutContent>

                        <AboutImageContainer>
                            <AboutImage src="https://images.unsplash.com/q=80&w=2000" alt="Terrasse en bois Decko" />
                        </AboutImageContainer>
                    </AboutGrid>
                </SectionContainer>
            </Section>

            <ExpertiseSection>
                <SectionContainer>
                    <SectionTitle text="Notre Expertise" centered />

                    <ExpertiseList>
                        <ExpertiseItem delay="0.1s">
                            <strong>L'écoute et l'interprétation fidèle de vos attentes</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.2s">
                            <strong>L'imagination de l'agencement extérieur et de l'ergonomie des espaces à créer</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.3s">
                            <strong>La connaissance des supports et des sols</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.4s">
                            <strong>La conception de la structure</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.5s">
                            <strong>La réalisation des plans</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.6s">
                            <strong>La connaissance des essences exotiques et indigènes, des matières composites, des métaux adaptés à l'extérieur</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.7s">
                            <strong>L'élaboration d'un cahier des charges très exigeant sur les choix, taux d'humidité des bois, de régularité des usinages et des calibrages. Le contrôle méthodique de chaque fabrication.</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.8s">
                            <strong>La maîtrise des techniques de pose testées et éprouvées notamment la pose sans vis apparentes</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="0.9s">
                            <strong>La qualité des finitions</strong>
                        </ExpertiseItem>
                        <ExpertiseItem delay="1s">
                            <strong>Les conseils et le suivi d'après-vente</strong>
                        </ExpertiseItem>
                    </ExpertiseList>

                    <ImagesGrid>
                        {[
                            { src: "https://images.unsplash.com/photo-1591364469468-6a1ddf320a85?q=80&w=600", alt: "Réalisation Decko 1", delay: "0.1s" },
                            { src: "https://images.unsplash.com/photo-1591364469468-6a1ddf320a85?q=80&w=600", alt: "Réalisation Decko 2", delay: "0.2s" },
                            { src: "https://images.unsplash.com/photo-1564501981099-4503086fb340?q=80&w=600", alt: "Réalisation Decko 3", delay: "0.3s" },
                            { src: "https://images.unsplash.com/photo-1628598211238-807a0b90f899?q=80&w=600", alt: "Réalisation Decko 4", delay: "0.4s" }
                        ].map((image, index) => (
                            <ImageItem key={index} delay={image.delay}>
                                <img src={image.src} alt={image.alt} />
                            </ImageItem>
                        ))}
                    </ImagesGrid>
                </SectionContainer>
            </ExpertiseSection>

        </EntreprisePageWrapper>
    );
};

export default Entreprise;